import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, FormControl, FormHelperText, Button, TextField, Divider, Box } from "@mui/material";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import ReactSelect from "react-select";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { selectCandidateTransferState } from "../../state/candidateTransfer/candidateTransferSelector";
import { getActiveCompanies, getSourceJobs, getSourceStages, getDestinationJobs, transferCandidate, moveCandidate, transferCandidateFailed } from "../../state/candidateTransfer/candidateTransferSlice";

const CandidateTransferModal = ({ isModalOpen, onClose, mode = "COPY" }) => {
  const dispatch = useDispatch();
  const [confirmData, setConfirmData] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isMoveMode = mode === "MOVE";

  const { companies, sourceJobs, sourceStages, destinationJobs, isLoadingCompanies, isLoadingSourceJobs, isLoadingSourceStages, isLoadingDestinationJobs, isTransferring, isTransferringSuccess } =
    useSelector(selectCandidateTransferState);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      sourceCompany: null,
      sourceJobId: null,
      sourceStage: null,
      limit: "",
      destinationCompany: null,
      destinationJobId: null,
      destinationStage: { value: 1, label: "Applications" },
    },
  });

  const watchSourceCompany = watch("sourceCompany");
  const watchSourceJobId = watch("sourceJobId");
  const watchDestinationCompany = watch("destinationCompany");

  useEffect(() => {
    if (isTransferringSuccess) {
      onClose();
      reset();
      setIsConfirmOpen(false);
      setConfirmData(null);
      dispatch(transferCandidateFailed());
    }
  }, [isTransferringSuccess, onClose, reset, dispatch]);

  useEffect(() => {
    if (isModalOpen && companies.length === 0) {
      dispatch(getActiveCompanies());
    }
  }, [isModalOpen, dispatch, companies.length]);

  useEffect(() => {
    if (watchSourceCompany?.value) {
      dispatch(getSourceJobs(watchSourceCompany.value));
    }
  }, [watchSourceCompany, dispatch]);

  useEffect(() => {
    if (watchSourceJobId?.value) {
      dispatch(getSourceStages(watchSourceJobId.value));
    }
  }, [watchSourceJobId, dispatch]);

  useEffect(() => {
    if (watchDestinationCompany?.value) {
      dispatch(getDestinationJobs(watchDestinationCompany.value));
    }
  }, [watchDestinationCompany, dispatch]);

  const formatCompanyOptions = (data) => data?.map((c) => ({ value: c.id, label: c.name })) || [];
  const formatJobOptions = (data) => data?.map((j) => ({ value: j.id, label: j.title })) || [];
  const formatStageOptions = (data) => data?.map((s) => ({ value: s.id || s, label: s.name || s })) || [];

  const onSubmit = (data) => {
    setConfirmData(data);
    setIsConfirmOpen(true);
  };

  const handleConfirmTransfer = () => {
    const data = confirmData;
    if (!data) return;

    const payload = {
      fromCompany: data.sourceCompany?.value,
      fromJob: data.sourceJobId?.value,
      fromStage: data.sourceStage?.value,
      fromStageName: data.sourceStage?.label,
      limit: data.limit ? Number(data.limit) : null,
      toCompany: data.destinationCompany?.value,
      toJob: data.destinationJobId?.value,
      toStage: data.destinationStage?.value,
      toStageName: data.destinationStage?.label,
    };

    if (isMoveMode) {
      dispatch(moveCandidate(payload));
    } else {
      dispatch(transferCandidate(payload));
    }
  };

  const handleClose = () => {
    onClose();
    reset();
    setIsConfirmOpen(false);
    setConfirmData(null);
  };

  return (
    <Dialog open={isModalOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isMoveMode ? "Move Candidates" : "Copy Candidates"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 0 }}>
            {/* Source Section */}
            <Grid item xs={12} md={5} pt={"0 !important"}>
              <Typography variant="h6" gutterBottom color="primary">
                Source (From)
              </Typography>
              <FormControl fullWidth margin="normal" error={!!errors.sourceCompany}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Company Name *
                </Typography>
                <Controller
                  name="sourceCompany"
                  control={control}
                  rules={{ required: "Source Company is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={formatCompanyOptions(companies)}
                      isLoading={isLoadingCompanies}
                      placeholder="Select Company Name"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.sourceCompany && <FormHelperText>{errors.sourceCompany.message}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.sourceJobId}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Select Job *
                </Typography>
                <Controller
                  name="sourceJobId"
                  control={control}
                  rules={{ required: "Source Job ID is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={formatJobOptions(sourceJobs)}
                      isLoading={isLoadingSourceJobs}
                      placeholder="Select Job"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.sourceJobId && <FormHelperText>{errors.sourceJobId.message}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.sourceStage}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Select Stage *
                </Typography>
                <Controller
                  name="sourceStage"
                  control={control}
                  rules={{ required: "Source Stage is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={[{ value: "All", label: "All" }, ...formatStageOptions(sourceStages)]}
                      isLoading={isLoadingSourceStages}
                      placeholder="Select Stage"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.sourceStage && <FormHelperText>{errors.sourceStage.message}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.limit}>
                <Controller
                  name="limit"
                  control={control}
                  rules={{
                    min: { value: 1, message: "Minimum 1 candidate" },
                  }}
                  render={({ field }) => <TextField {...field} label="Transfer Limit" type="number" size="small" error={!!errors.limit} />}
                />
                {errors.limit && <FormHelperText>{errors.limit.message}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Icon Section */}
            <Grid item pt={0} xs={12} md={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
              <DoubleArrowIcon color="action" sx={{ fontSize: 48, display: { xs: "none", md: "block" } }} />
              <Divider sx={{ display: { xs: "block", md: "none" }, my: 2, width: "100%" }} />
            </Grid>

            {/* Destination Section */}
            <Grid item xs={12} md={5} pt={"0 !important"}>
              <Typography variant="h6" gutterBottom color="secondary">
                Destination (To)
              </Typography>
              <FormControl fullWidth margin="normal" error={!!errors.destinationCompany}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Company Name *
                </Typography>
                <Controller
                  name="destinationCompany"
                  control={control}
                  rules={{ required: "Destination Company is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={formatCompanyOptions(companies)}
                      isLoading={isLoadingCompanies}
                      placeholder="Select Company Name"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.destinationCompany && <FormHelperText>{errors.destinationCompany.message}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.destinationJobId}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Select Job *
                </Typography>
                <Controller
                  name="destinationJobId"
                  control={control}
                  rules={{ required: "Destination Job ID is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={formatJobOptions(destinationJobs)}
                      isLoading={isLoadingDestinationJobs}
                      placeholder="Select Job"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.destinationJobId && <FormHelperText>{errors.destinationJobId.message}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.destinationStage}>
                <Typography variant="caption" sx={{ mb: 0.5 }}>
                  Stage *
                </Typography>
                <Controller
                  name="destinationStage"
                  control={control}
                  rules={{ required: "Destination Stage is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={[{ value: 1, label: "Applications" }]}
                      isDisabled={true}
                      placeholder="Select Stage"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                />
                {errors.destinationStage && <FormHelperText>{errors.destinationStage.message}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" type="submit" disabled={!isValid || isTransferring}>
            Proceed
          </Button>
        </DialogActions>
      </form>

      {/* Confirmation Alert Dialog */}
      <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isMoveMode ? "Confirm Move" : "Confirm Copy"}</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>Are you sure you want to {isMoveMode ? "move" : "copy"} candidates?</Typography>
          {confirmData && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>From:</strong> {confirmData.sourceCompany?.label} - {confirmData.sourceJobId?.label}
                <br />
                <strong>Stage:</strong> {confirmData.sourceStage?.label}
              </Typography>
              <Typography variant="body2" color="error" sx={{ my: 1 }}>
                ↓ To
              </Typography>
              <Typography variant="body2">
                <strong>To:</strong> {confirmData.destinationCompany?.label} - {confirmData.destinationJobId?.label}
                <br />
                <strong>Stage:</strong> {confirmData.destinationStage?.label}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmTransfer} disabled={isTransferring}>
            {isTransferring ? (isMoveMode ? "Moving..." : "Copying...") : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CandidateTransferModal;
